import type { Resource } from '@intake24/admin/types';

const routes: string[] = ['create', 'read', 'edit'];

export const resources: Resource[] = [
  {
    group: 'user',
    name: 'user.jobs',
    icon: 'fas fa-circle-user',
    api: 'admin/user/jobs',
    generateRoutes: false,
    routes: ['create', 'read'],
  },
  // Food databases
  {
    group: 'fdb',
    name: 'fdbs',
    module: 'locales',
    icon: 'fas fa-drumstick-bite',
    api: 'admin/fdbs',
    generateRoutes: false,
    routes: ['food-list'],
    refs: true,
  },
  {
    group: 'fdb',
    name: 'nutrient-tables',
    icon: 'fas fa-table-cells',
    api: 'admin/nutrient-tables',
    generateRoutes: true,
    routes: [...routes, 'tasks'],
    refs: true,
  },
  {
    group: 'fdb',
    name: 'nutrient-types',
    icon: 'fas fa-seedling',
    api: 'admin/nutrient-types',
    generateRoutes: true,
    routes: [...routes],
    refs: true,
  },
  {
    group: 'fdb',
    name: 'nutrient-units',
    icon: 'fas fa-calculator',
    api: 'admin/nutrient-units',
    generateRoutes: true,
    routes: [...routes],
  },
  // Localization
  {
    group: 'local',
    name: 'languages',
    icon: 'fas fa-language',
    api: 'admin/languages',
    generateRoutes: true,
    securable: true,
    routes: [...routes, 'translations', 'securables'],
  },
  {
    group: 'local',
    name: 'locales',
    icon: 'fas fa-map-marked-alt',
    api: 'admin/locales',
    generateRoutes: true,
    securable: true,
    routes: [
      ...routes,
      'split-lists',
      'split-words',
      'synonym-sets',
      'standard-units',
      'food-builders',
      'tasks',
      'securables',
    ],
    refs: true,
  },
  // Surveys
  {
    group: 'surveyMgmt',
    name: 'faqs',
    icon: 'fas fa-circle-question',
    api: 'admin/faqs',
    securable: true,
    generateRoutes: true,
    routes: [
      ...routes,
      'media',
      'securables',
    ],
  },
  {
    group: 'surveyMgmt',
    name: 'feedback-schemes',
    icon: 'fas fa-comment-dots',
    api: 'admin/feedback-schemes',
    securable: true,
    generateRoutes: true,
    routes: [
      ...routes,
      'top-foods',
      'meals',
      'cards',
      'demographic-groups',
      'henry-coefficients',
      'media',
      'securables',
    ],
    refs: true,
  },
  {
    group: 'surveyMgmt',
    name: 'survey-schemes',
    icon: 'fas fa-route',
    api: 'admin/survey-schemes',
    securable: true,
    generateRoutes: true,
    routes: [...routes, 'prompts', 'data-export', 'media', 'securables'],
    refs: true,
  },
  {
    group: 'surveyMgmt',
    name: 'survey-scheme-prompts',
    icon: 'far fa-question-circle',
    api: 'admin/survey-scheme-prompts',
    generateRoutes: true,
    routes: [...routes, 'sync'],
    refs: true,
  },
  {
    group: 'surveyMgmt',
    name: 'surveys',
    icon: 'fas fa-poll',
    api: 'admin/surveys',
    securable: true,
    generateRoutes: true,
    routes: [
      ...routes,
      'overrides',
      'respondents',
      'submissions',
      'sessions',
      'tasks',
      'securables',
    ],
  },
  // Images
  {
    group: 'images',
    name: 'as-served-sets',
    path: 'images/as-served-sets',
    icon: 'fas fa-utensils',
    api: 'admin/images/as-served-sets',
    generateRoutes: true,
    routes: [...routes],
  },
  {
    group: 'images',
    name: 'image-maps',
    path: 'images/image-maps',
    icon: 'fas fa-draw-polygon',
    api: 'admin/images/image-maps',
    generateRoutes: true,
    routes: [...routes],
  },
  {
    group: 'images',
    name: 'guide-images',
    path: 'images/guide-images',
    icon: 'fas fa-crosshairs',
    api: 'admin/images/guide-images',
    generateRoutes: true,
    routes: [...routes],
  },
  {
    group: 'images',
    name: 'drinkware-sets',
    path: 'images/drinkware-sets',
    icon: 'fas fa-mug-saucer',
    api: 'admin/images/drinkware-sets',
    generateRoutes: true,
    routes: [...routes],
  },
  {
    group: 'images',
    name: 'media',
    icon: 'fas fa-photo-film',
    api: 'admin/media',
    generateRoutes: true,
    routes: ['read', 'edit'],
  },
  // System
  {
    group: 'system',
    name: 'jobs',
    icon: 'fas fa-running',
    api: 'admin/jobs',
    generateRoutes: true,
    routes: ['read'],
  },
  {
    group: 'system',
    name: 'sign-in-logs',
    icon: 'fas fa-sign-in-alt',
    api: 'admin/sign-in-logs',
    generateRoutes: true,
    routes: ['read'],
  },
  {
    group: 'system',
    name: 'tasks',
    icon: 'fas fa-tasks',
    api: 'admin/tasks',
    generateRoutes: true,
    routes: [...routes],
  },
  {
    group: 'system',
    name: 'audit',
    icon: 'fas fa-timeline',
    api: 'admin/audit/tables',
    generateRoutes: false,
    routes: ['tables'],
  },
  // ACL
  {
    group: 'acl',
    name: 'users',
    icon: 'fas fa-fw fa-users',
    api: 'admin/users',
    generateRoutes: true,
    routes: [...routes, 'permissions', 'roles', 'securables'],
    refs: true,
  },
  {
    group: 'acl',
    name: 'roles',
    icon: 'far fa-fw fa-id-badge',
    api: 'admin/roles',
    generateRoutes: true,
    routes: [...routes, 'permissions', 'users'],
    refs: true,
  },
  {
    group: 'acl',
    name: 'permissions',
    icon: 'far fa-fw fa-eye-slash',
    api: 'admin/permissions',
    generateRoutes: true,
    routes: [...routes, 'roles', 'users'],
  },
  // Food database package import/export
  {
    group: 'fdb',
    name: 'packages.import',
    module: 'packages:import',
    icon: 'fas fa-fw fa-download',
    title: 'io.import._',
    api: 'admin/packages',
    generateRoutes: false,
    routes: [],
  },
  {
    group: 'fdb',
    name: 'packages.export',
    module: 'packages:export',
    icon: 'fas fa-fw fa-upload',
    title: 'io.export._',
    api: 'admin/packages',
    generateRoutes: false,
    routes: [],
  },
];

export const getResource = (name: string) => resources.find(resource => resource.name === name);

export default resources;
